"use client";

import { useCallback, useSyncExternalStore } from "react";

export const MOBILE_MAX_WIDTH_PX = 768;
/** Phones held sideways: wide but short touch screens. Mirrors postcss-short-landscape-mobile.cjs. */
const SHORT_TOUCH_VIEWPORT_MEDIA_QUERY =
  "(max-height: 499.98px) and (pointer: coarse)";
export const MOBILE_VIEWPORT_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH_PX - 1}px), ${SHORT_TOUCH_VIEWPORT_MEDIA_QUERY}`;

/** Synchronous mobile check (client only). */
export function matchesMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY).matches;
}

/**
 * True when `query` matches.
 * False on the server (no hydration mismatch) and while printing (keep desktop chrome).
 */
function useViewportQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void): (() => void) => {
      const mq: MediaQueryList = window.matchMedia(query);
      const printMq: MediaQueryList = window.matchMedia("print");
      const onViewportChange = (): void => {
        onStoreChange();
      };
      mq.addEventListener("change", onViewportChange);
      printMq.addEventListener("change", onViewportChange);
      window.addEventListener("beforeprint", onViewportChange);
      window.addEventListener("afterprint", onViewportChange);
      return (): void => {
        mq.removeEventListener("change", onViewportChange);
        printMq.removeEventListener("change", onViewportChange);
        window.removeEventListener("beforeprint", onViewportChange);
        window.removeEventListener("afterprint", onViewportChange);
      };
    },
    [query],
  );
  const read = useCallback((): boolean => {
    if (window.matchMedia("print").matches) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, read, (): boolean => false);
}

/** True when viewport is narrower than `widthPx`. */
export function useViewportBelow(widthPx: number): boolean {
  return useViewportQuery(`(max-width: ${widthPx - 1}px)`);
}

/** True below the mobile breakpoint or on a landscape phone (matches Tailwind `md`). */
export function useMobileViewport(): boolean {
  return useViewportQuery(MOBILE_VIEWPORT_MEDIA_QUERY);
}
